import { httpServiceClient } from '@/infra/http'

import { formatDependentRequestList } from './dependent-service-utils'
import { makeApiUrl } from '@/main/factories/http/api-url-factory'
import { EnrollmentStatusTab } from '@/domain/models/enrollment/status'
import { formatCustomDateString } from '@/main/helpers'
import {
  DependentCreateRequest,
  DependentCreateResponse,
  DependentEnrollmentQuery,
  DependentEnrollmentResponse,
  DependentPendingRequest
} from './dependent-service-types'

const getDependentRequestUrl = (companyId: string) =>
  makeApiUrl(`/customer/v1/company/${companyId}`)

export const createDependentRequestService = (client = httpServiceClient) => ({
  async fetchPendingRequests(companyId: string) {
    const pathUrl = `${getDependentRequestUrl(companyId)}/dependent-request/pending`

    const response = await client<DependentPendingRequest>({
      method: 'get',
      url: pathUrl
    })

    return response.body.data as DependentPendingRequest
  },

  async createRequest(companyId: string, payload: DependentCreateRequest) {
    const pathUrl = `${getDependentRequestUrl(companyId)}/dependent-request`

    const response = await client<DependentCreateResponse>({
      method: 'post',
      url: pathUrl,
      body: {
        ...payload,
        birthDate: formatCustomDateString(payload.birthDate, 'yyyy-mm-dd')
      }
    })

    return response.body.data as DependentCreateResponse
  },

  async cancelRequest(companyId: string, requestUuid: string) {
    const pathUrl = `${getDependentRequestUrl(companyId)}/dependent-request/${requestUuid}/cancel`

    await client<void>({
      method: 'put',
      url: pathUrl
    })
  },

  async fetchRequestList(companyId: string, payload: DependentEnrollmentQuery) {
    const pathUrl = `${getDependentRequestUrl(companyId)}/dependent-request`

    const response = await client<DependentEnrollmentResponse>({
      method: 'get',
      url: pathUrl,
      params: {
        page: String(payload.page ?? 1),
        pageSize: String(payload.pageSize ?? 10),
        status: payload.status || EnrollmentStatusTab.WAITING_HR,
        search: payload.search || undefined
      }
    })

    return formatDependentRequestList(response.body.data as DependentEnrollmentResponse)
  },

  async approveRequest(companyId: string, requestUuid: string, chargeType: string) {
    const pathUrl = `${getDependentRequestUrl(companyId)}/dependent-request/${requestUuid}/approve`

    await client<void>({
      method: 'put',
      url: pathUrl,
      body: {
        charge_type: chargeType
      }
    })
  },

  async rejectRequest(companyId: string, requestUuid: string, reason: string) {
    const pathUrl = `${getDependentRequestUrl(companyId)}/dependent-request/${requestUuid}/reject`

    await client<void>({
      method: 'put',
      url: pathUrl,
      body: { reason }
    })
  }
})

export const dependentRequestService = createDependentRequestService()
